import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLenis } from "lenis/react";
import { IMAGES, PRICE_LIST } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const LASER = PRICE_LIST.find((c) => c.id === "laser");

export default function LaserTreatments() {
  const lenis = useLenis();

  return (
    <section id="laser" data-testid="laser-section" className="bg-ink py-28 text-blush-50 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500"
        >
          <span className="h-px w-10 bg-blush-500" />
          Trattamenti laser
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="max-w-3xl font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl"
        >
          Cosa sono i <span className="italic text-blush-500">trattamenti laser?</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          data-testid="laser-intro"
          className="mt-8 max-w-3xl text-base leading-relaxed text-blush-50/70"
        >
          Il trattamento laser sfrutta l'energia della luce mirata per agire in modo
          selettivo sulla pelle o sui pigmenti sottocutanei, stimolando la
          rigenerazione cellulare o frammentando i colori indesiderati (come nel caso
          dei tatuaggi e del trucco semipermanente), garantendo precisione, sicurezza
          e risultati progressivi.
        </motion.p>

        <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <ul className="border-t border-white/10">
              {LASER.items.map((item, i) => (
                <motion.li
                  key={i}
                  data-testid={`laser-item-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.06 }}
                  className="group flex items-baseline gap-3 border-b border-white/10 py-6"
                >
                  <div className="min-w-0">
                    <p className="font-serif text-xl italic text-blush-50 transition-colors duration-300 group-hover:text-blush-500 lg:text-2xl">
                      {item.name}
                    </p>
                    {item.note && <p className="mt-1.5 text-xs leading-relaxed text-blush-50/60">{item.note}</p>}
                  </div>
                  <span aria-hidden className="mx-2 flex-1 -translate-y-1 border-b border-dotted border-blush-500/40" />
                  <span data-testid={`laser-price-${i}`} className="shrink-0 font-serif text-xl italic text-blush-500 lg:text-2xl">
                    {item.price}
                  </span>
                </motion.li>
              ))}
            </ul>

            <motion.div
              data-testid="laser-consulto-note"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: EASE }}
              className="mt-10 flex items-start gap-4 rounded-2xl border border-blush-500/30 bg-blush-500/10 p-6"
            >
              <ShieldCheck size={22} className="mt-0.5 shrink-0 text-blush-500" />
              <p className="text-sm leading-relaxed text-blush-50/80">
                Tutti i trattamenti laser vengono effettuati o confermati previa
                valutazione in sede di consulto.
              </p>
            </motion.div>

            <motion.a
              href="#prenota"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#prenota", { duration: 1.8 }); }}
              data-testid="laser-cta-consulto"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-blush-500 px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-500 hover:bg-blush-600"
            >
              Prenota il tuo consulto
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </motion.a>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="grid grid-cols-2 gap-4"
            >
              <figure data-testid="laser-photo-prima">
                <div className="overflow-hidden rounded-2xl border border-white/10" style={{ aspectRatio: "3/4" }}>
                  <img src={IMAGES.laserPrima} alt="Sopracciglia con tatuaggio prima del laser" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
                </div>
                <figcaption className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Prima
                </figcaption>
              </figure>
              <figure data-testid="laser-photo-dopo" className="mt-10">
                <div className="overflow-hidden rounded-2xl border border-white/10" style={{ aspectRatio: "3/4" }}>
                  <img src={IMAGES.laserDopo} alt="Sopracciglia dopo le sedute laser di rimozione" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
                </div>
                <figcaption className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-50/60">
                  Dopo le sedute
                </figcaption>
              </figure>
            </motion.div>
            <p className="mt-6 text-center text-[10px] font-semibold uppercase tracking-[0.35em] text-blush-500">
              Rimozione tattoo sopracciglia — progressione sedute laser
            </p>
          </div>
        </div>

        <motion.figure
          data-testid="laser-hollywood-photo"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-20"
        >
          <div className="overflow-hidden rounded-3xl border border-white/10" style={{ aspectRatio: "16/9" }}>
            <img src={IMAGES.hollywood} alt="Trattamento Hollywood Peel con maschera al carbonio" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
          </div>
          <figcaption className="mt-5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-blush-50/60">
            <span className="h-px w-6 bg-blush-500" />
            Hollywood Peel — il "carbon laser facial": pelle luminosa, pori affinati, incarnato uniforme
          </figcaption>
        </motion.figure>
      </div>
    </section>
  );
}
